import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type {
  DisabledParameterField,
  FieldConfig,
  FilterGroup,
  IbeFilterConfiguration,
  IbeSearchDefaults,
  ParameterField,
  SearchDefaultFilter,
} from '@/types/SearchDefaults';
import type { ResolvedFilterGroup, ResolvedFilterOption } from '@/types/FilterBar';
import type { DurationOption } from '@/types/SearchBar';
import { $t } from '@/composables/translations/useTranslate';
import { FilterValidation } from '@/utils/validation/filterValidation';

interface ResolvedDefaultFilter {
  groupId: string;
  visible: boolean;
  presets: string[];
  forcedKeys: string[];
}

export function isFieldForced<T>(cfg: FieldConfig<T> | undefined): boolean {
  return cfg?.['override-user'] === true;
}

/**
 * Filter Configuration Store
 *
 * Holds the curated filter configuration delivered via
 * `window.ibeFilterConfiguration`, the per-page filter overrides from
 * `window.ibeSearchDefaults.filters`, and trip-duration options.
 *
 * Every group declared in `ibeFilterConfiguration` is **visible by default**.
 * `ibeSearchDefaults.filters` is an overrides list, not an allowlist: an
 * entry can hide a group (`visible: false`) or force preset options
 * (non-empty `presets`). Groups without an override stay visible.
 *
 * The store is the single source of truth for:
 * - Which filter groups exist and in what order
 * - Which options belong to each group and how they map to HUB values
 * - Which groups are hidden, disabled, or editable on this page
 * - Which composite keys are forced (set once at init, merged into apiParams)
 * - Which search-bar parameter fields are forced (disabled in the UI)
 *
 * It intentionally knows nothing about translations or selection state;
 * callers combine `resolvedGroups()` with label resolution (done via `$t` in
 * the component layer) and with `selectedAccommodationProperties` from
 * `productListFilters` to render the UI.
 */
export const useFilterConfigurationStore = defineStore('filterConfiguration', () => {
  const filterGroups = ref<FilterGroup[]>([]);
  const durations = ref<number[]>([]);
  const defaultDuration = ref<number>(7);
  const searchDefaultFilters = ref<SearchDefaultFilter[]>([]);
  const forcedParameterFields = ref<Set<ParameterField>>(new Set());

  /**
   * UI-facing subset of forced parameters. `date`, `todate`, and `days` all
   * collapse into a single `'date'` button in the SearchBar, so forcing any
   * of them disables that button.
   */
  const disabledParameterFields = computed<DisabledParameterField[]>(() => {
    const out: DisabledParameterField[] = [];
    if (forcedParameterFields.value.has('dest')) out.push('dest');
    if (
      forcedParameterFields.value.has('date') ||
      forcedParameterFields.value.has('todate') ||
      forcedParameterFields.value.has('days')
    ) {
      out.push('date');
    }
    if (forcedParameterFields.value.has('pax')) out.push('pax');
    return out;
  });

  const durationOptions = computed<DurationOption[]>(() => {
    const options: DurationOption[] = [
      { value: 0, label: $t('ibe.duration.exact', 'Exact'), type: 'exact' },
    ];

    const effectiveDurations = durations.value.length > 0 ? durations.value : [3, 5, 7, 10, 14, 21];

    effectiveDurations.forEach((days) => {
      let label = '';
      switch (days) {
        case 3:
          label = $t('ibe.duration.3-days', '3 days');
          break;
        case 5:
          label = $t('ibe.duration.5-days', '5 days');
          break;
        case 7:
          label = $t('ibe.duration.1-week', '1 week');
          break;
        case 10:
          label = $t('ibe.duration.10-days', '10 days');
          break;
        case 14:
          label = $t('ibe.duration.2-weeks', '2 weeks');
          break;
        case 21:
          label = $t('ibe.duration.3-weeks', '3 weeks');
          break;
        default:
          label = $t('ibe.duration.n-days', '{n} days', { n: days });
      }

      options.push({
        value: days,
        label,
        type: 'preset',
      });
    });

    return options;
  });

  /**
   * Single point of truth for filter behaviour: resolves each entry in the
   * `searchDefaultFilters` allowlist against `filterGroups`
   */
  const resolvedDefaultFilters = computed<ResolvedDefaultFilter[]>(() => {
    return searchDefaultFilters.value.flatMap((entry) => {
      const group = filterGroups.value.find((g) => g.id === entry.filter);
      if (!group) return [];

      const forcedKeys: string[] = [];
      for (const optionId of entry.presets) {
        const opt = group.options.find((o) => o.id === optionId);
        if (!opt) continue;
        forcedKeys.push(`${opt.hubProperty}.${opt.hubValue}`);
      }

      return [
        {
          groupId: entry.filter,
          visible: entry.visible,
          presets: entry.presets,
          forcedKeys,
        },
      ];
    });
  });

  /**
   * Groups that render but cannot be toggled — `visible: true` AND non-empty
   * `presets`. Their checkboxes are visible-but-disabled in the UI.
   */
  const disabledGroupIds = computed<Set<string>>(() => {
    const s = new Set<string>();
    for (const entry of resolvedDefaultFilters.value) {
      if (entry.visible && entry.forcedKeys.length > 0) s.add(entry.groupId);
    }
    return s;
  });

  /**
   * Groups that don't render at all. A group is hidden only when an override
   * entry in `ibeSearchDefaults.filters` sets `visible: false`. Groups
   * without an override entry stay visible by default.
   */
  const hiddenGroupIds = computed<Set<string>>(() => {
    const hidden = new Set<string>();
    for (const entry of resolvedDefaultFilters.value) {
      if (!entry.visible) hidden.add(entry.groupId);
    }
    return hidden;
  });

  /**
   * All composite keys forced by `presets` (whether hidden or disabled-visible).
   */
  const forcedCompositeKeys = computed<string[]>(() => {
    const all = resolvedDefaultFilters.value.flatMap((entry) => entry.forcedKeys);
    return [...new Set(all)];
  });

  function loadFromDefaults(defaults: IbeSearchDefaults): void {
    if (defaults.durations) {
      durations.value = defaults.durations;
    }

    const validatedDefaultDuration = FilterValidation.validateDuration(defaults.days?.value);
    if (validatedDefaultDuration !== null) {
      defaultDuration.value = validatedDefaultDuration;
    }

    searchDefaultFilters.value = defaults.filters ?? [];

    const forced = new Set<ParameterField>();
    if (isFieldForced(defaults.dest)) forced.add('dest');
    if (isFieldForced(defaults.date)) forced.add('date');
    if (isFieldForced(defaults.todate)) forced.add('todate');
    if (isFieldForced(defaults.days)) forced.add('days');
    if (isFieldForced(defaults.pax)) forced.add('pax');
    forcedParameterFields.value = forced;
  }

  function loadFromFilterConfiguration(config: IbeFilterConfiguration): void {
    filterGroups.value = config.filters;
  }

  /**
   * Resolve the configured groups against the composite keys currently
   * returned by the Player API. Hidden groups (and groups with all options
   * filtered out) are excluded; disabled-visible groups are returned with
   * `disabled: true` so the UI can render them as read-only.
   */
  function resolvedGroups(availableKeys: Set<string>): ResolvedFilterGroup[] {
    const resolved: ResolvedFilterGroup[] = [];
    const hidden = hiddenGroupIds.value;
    const disabled = disabledGroupIds.value;

    for (const group of filterGroups.value) {
      if (hidden.has(group.id)) continue;

      const options: ResolvedFilterOption[] = [];
      for (const opt of group.options) {
        const compositeKey = `${opt.hubProperty}.${opt.hubValue}`;
        if (!availableKeys.has(compositeKey)) continue;

        options.push({
          groupId: group.id,
          optionId: opt.id,
          compositeKey,
          groupLabelKey: `ibe.filters.${group.id}.label`,
          optionLabelKey: `ibe.filters.${group.id}.${opt.id}.label`,
        });
      }

      if (options.length === 0) continue;

      resolved.push({
        id: group.id,
        labelKey: `ibe.filters.${group.id}.label`,
        options,
        disabled: disabled.has(group.id),
      });
    }

    return resolved;
  }

  /**
   * URL → store. Translate `{groupId}_{optionId}` tokens from the `att` query
   * parameter into composite keys. Tokens for hidden or disabled-forced groups
   * are dropped (mirrors the localStorage cleansing rule). Unknown group/option
   * ids are also dropped silently. Output is deduped, first-seen order.
   */
  function resolveCompositeKeysForTokens(tokens: string[]): string[] {
    const hidden = hiddenGroupIds.value;
    const disabled = disabledGroupIds.value;
    const seen = new Set<string>();
    const result: string[] = [];

    for (const token of tokens) {
      const sep = token.indexOf('_');
      if (sep <= 0 || sep === token.length - 1) continue;

      const groupId = token.slice(0, sep);
      const optionId = token.slice(sep + 1);

      if (hidden.has(groupId) || disabled.has(groupId)) continue;

      const group = filterGroups.value.find((g) => g.id === groupId);
      if (!group) continue;

      const opt = group.options.find((o) => o.id === optionId);
      if (!opt) continue;

      const compositeKey = `${opt.hubProperty}.${opt.hubValue}`;
      if (seen.has(compositeKey)) continue;
      seen.add(compositeKey);
      result.push(compositeKey);
    }

    return result;
  }

  /**
   * Store → URL. For each composite key, emit the first `{groupId}_{optionId}`
   * pair whose option matches, iterating groups in config order. Composites
   * with no matching option are dropped — they are not user-visible filters
   * and persisting them in the URL has no value.
   */
  function resolveTokensForCompositeKeys(compositeKeys: string[]): string[] {
    const result: string[] = [];

    for (const compositeKey of compositeKeys) {
      let matched: string | null = null;

      for (const group of filterGroups.value) {
        const opt = group.options.find((o) => `${o.hubProperty}.${o.hubValue}` === compositeKey);
        if (opt) {
          matched = `${group.id}_${opt.id}`;
          break;
        }
      }

      if (matched) result.push(matched);
    }

    return result;
  }

  /**
   * For a composite key, return the id of the group it belongs to (first
   * match in config order). Returns `null` for keys that don't map to any
   * configured option — used by `loadFromLocalStorage` to drop stale keys
   * and decide which group's hidden/disabled set to consult.
   */
  function findGroupIdForCompositeKey(compositeKey: string): string | null {
    for (const group of filterGroups.value) {
      const match = group.options.find((o) => `${o.hubProperty}.${o.hubValue}` === compositeKey);
      if (match) return group.id;
    }
    return null;
  }

  return {
    // State
    filterGroups,
    durations,
    defaultDuration,
    searchDefaultFilters,
    forcedParameterFields,

    // Computed
    durationOptions,
    disabledParameterFields,
    resolvedDefaultFilters,
    disabledGroupIds,
    hiddenGroupIds,
    forcedCompositeKeys,

    // Actions
    loadFromDefaults,
    loadFromFilterConfiguration,

    // Selectors
    resolvedGroups,
    resolveCompositeKeysForTokens,
    resolveTokensForCompositeKeys,
    findGroupIdForCompositeKey,
  };
});
